import { getCookie, setCookie } from "cookies-next";
import { usePathname } from "next/navigation";
import {
  useState,
  useEffect,
  useCallback,
  startTransition,
  useRef,
} from "react";

import useAnalytics from "../../../hooks/useAnalytics";
import {
  OneClickSignInDialogDisplayedEvent,
  OneClickSignInOptInEvent,
  OneClickSignInOptOutEvent,
} from "../../../telemetry/analyticsEvents";

import {
  ONE_CLICK_SIGN_IN_LOGIN_HISTORY_COOKIE,
  ONE_CLICK_SIGN_IN_COOKIE,
  ONE_CLICK_SIGN_IN_LOGIN_PAGE_URL,
  ONE_CLICK_SIGN_IN_MAX_AGE_MS,
  ONE_CLICK_SIGN_IN_MIN_LOGIN_COUNT,
  ONE_CLICK_SIGN_IN_OPT_IN_COOKIE_EXPIRY_DAYS,
  ONE_CLICK_SIGN_IN_OPT_OUT_COOKIE_EXPIRY_DAYS,
} from "./constants";

interface UseOneClickSignInResult {
  shouldShowPopup: boolean;
  optIn: () => void;
  optOut: () => void;
}

const cookieOptions = {
  path: "/",
  domain: process.env.NODE_ENV === "production" ? ".brex.com" : "localhost", // cross-subdomain access
  secure: process.env.NODE_ENV === "production" ? true : false, // Ensure cookies are sent over HTTPS only
  sameSite: "lax" as const, // Use 'lax' or 'none'. 'strict' might be too restrictive.
};

/**
 * Hook to manage the "One Click Login" feature using cookies-next.
 * Checks login history and opt-in status from cookies.
 * Provides state to show an opt-in popup and a function to handle opting in.
 * Handles automatic redirection if already opted in.
 * @param redirectPath - The path to perform the redirect from if its restricted to a specific path
 */
export const useOneClickSignIn = (
  redirectPath = "/",
): UseOneClickSignInResult => {
  const path = usePathname();

  const [showPopup, setShowPopup] = useState<boolean>(false);
  const eventTriggeredRef = useRef(false);
  const { trackEvent } = useAnalytics();

  // Check cookies on mount
  useEffect(() => {
    // Base Case, if hook is running on a page that is not the redirect path, do nothing
    if (path !== redirectPath) {
      return;
    }

    // 1. Check if user has interacted with the feature before
    const oneClickCookieValue = getCookie(ONE_CLICK_SIGN_IN_COOKIE);
    // Check if explicitly opted-out
    if (oneClickCookieValue === false) {
      return;
    }

    // If cookie is undefined or any other value, proceed to history check...

    // 2. Check login history if not opted-in/out
    const historyCookie = getCookie(ONE_CLICK_SIGN_IN_LOGIN_HISTORY_COOKIE);
    if (!historyCookie || typeof historyCookie !== "string") {
      return; // No history or invalid type, do nothing
    }

    try {
      const historyTimestamps = JSON.parse(historyCookie);
      if (!Array.isArray(historyTimestamps)) {
        console.warn(
          "Invalid _signInHistory cookie format (not an array). Exiting check.",
        );
        return;
      }

      const now = Date.now();
      const recentLogins = historyTimestamps.filter(
        (ts) =>
          typeof ts === "number" && now - ts < ONE_CLICK_SIGN_IN_MAX_AGE_MS,
      );

      // 3. Check if criteria met (>= MIN_LOGIN_COUNT logins in MAX_AGE_MS)
      const meetsCriteria =
        recentLogins.length >= ONE_CLICK_SIGN_IN_MIN_LOGIN_COUNT;
      if (meetsCriteria) {
        startTransition(() => {
          setShowPopup(true);
        });
      }
    } catch (error) {
      console.error("Error parsing _signInHistory cookie:", error);
    }
  }, [path, redirectPath, trackEvent]); // Run on mount

  // Hook to hide the popup if the user navigates away from the redirect path
  useEffect(() => {
    if (path !== redirectPath && showPopup) {
      setShowPopup(false);
    }
  }, [path, redirectPath, showPopup]);

  const optIn = useCallback(() => {
    // 1. Set the opt-in cookie
    const expiryDate = new Date();
    expiryDate.setDate(
      expiryDate.getDate() + ONE_CLICK_SIGN_IN_OPT_IN_COOKIE_EXPIRY_DAYS,
    );

    trackEvent(new OneClickSignInOptInEvent());

    setCookie(ONE_CLICK_SIGN_IN_COOKIE, true, {
      ...cookieOptions,
      expires: expiryDate,
    });

    // 2. Hide the popup
    setShowPopup(false);

    // 3. Redirect to the login page
    window.location.assign(ONE_CLICK_SIGN_IN_LOGIN_PAGE_URL);
  }, [trackEvent]);

  const optOut = useCallback(() => {
    // 1. Set Cookie
    const expiryDate = new Date();
    expiryDate.setDate(
      expiryDate.getDate() + ONE_CLICK_SIGN_IN_OPT_OUT_COOKIE_EXPIRY_DAYS,
    );

    trackEvent(new OneClickSignInOptOutEvent());

    setCookie(ONE_CLICK_SIGN_IN_COOKIE, false, {
      ...cookieOptions,
      expires: expiryDate,
    });

    // 2. Hide the popup
    setShowPopup(false);
  }, [trackEvent]);

  // Statsig Track Event when Dialog shown
  useEffect(() => {
    if (showPopup && !eventTriggeredRef.current) {
      eventTriggeredRef.current = true;
      trackEvent(new OneClickSignInDialogDisplayedEvent());
    }
  }, [showPopup, trackEvent]);

  return { shouldShowPopup: showPopup, optIn, optOut };
};
